import { isSameMonth, startOfMonth, subMonths, UTCDate } from '@/utils/dates';
import { Big } from 'big.js';
import { ArrowDown, ArrowRight, ArrowUp } from 'lucide-react';
import { TransactionView } from '../../../types/entity-types';
import { SupportedCountryCurrency } from '../../../types/supported-country-currency';
import { isInternalTransfer } from './is-internal-transfer';

export type MonthlyAmountBreakdown = {
  currency: SupportedCountryCurrency;
  totalAmount: number;
  currentMonthAmount: number;
  previousMonthAmount: number;
  twoMonthsAgoAmount: number;
  threeMonthsAgoAmount: number;
  monthOverMonthIncrease: number;
};

/**
 * Formats month-over-month change for display in the reporting table
 * Works consistently for expenses (negative), revenue (positive), and net cash (either)
 * Since expenses are already negative in the table, all trends can be treated equally:
 * - Positive change = improvement → positive percentage (green, up arrow)
 * - Negative change = deterioration → negative percentage (red, down arrow)
 * @param change - The monthOverMonthIncrease value (current - previous or previous - twoMonthsAgo)
 * @param baseAmount - The base amount for percentage calculation (previous month or two months ago)
 * @returns Formatted MoM change with percentage, sign, and arrow
 */
export const formatMomChange = (change: number, baseAmount: number) => {
  const displayChange = change; // No sign inversion - treat all trends equally
  const isPositive = displayChange > 0;
  const isNegative = displayChange < 0;
  const isZero = displayChange === 0;

  // Calculate percentage change with proper zero handling
  let percentageChange = 0;

  if (isZero) {
    percentageChange = 0;
  } else if (Math.abs(baseAmount) < 0.01) {
    // Handle zero base amount cases:
    // 0 → positive: +100%
    // 0 → negative: -100%
    // negative → 0: +100% (change is positive: 0 - negative = positive)
    // positive → 0: -100% (change is negative: 0 - positive = negative)
    percentageChange = isPositive ? 100 : -100;
  } else {
    // Normal percentage calculation
    percentageChange = (displayChange / Math.abs(baseAmount)) * 100;
  }

  return {
    percentageChange: Math.round(percentageChange),
    isPositive,
    isNegative,
    isZero,
    colorClass: isPositive ? 'text-success' : isNegative ? 'text-destructive' : 'text-muted-foreground',
    arrow: isPositive ? ArrowUp : isNegative ? ArrowDown : ArrowRight,
    sign: isPositive ? '+' : '',
  };
};

export const calculateMonthlyBreakdown = (
  transactions: TransactionView[] = [],
  baseCurrency: SupportedCountryCurrency,
  referenceDate: Date = new UTCDate(),
): MonthlyAmountBreakdown => {
  const currentMonth = startOfMonth(referenceDate);
  const previousMonth = startOfMonth(subMonths(currentMonth, 1));
  const twoMonthsAgo = startOfMonth(subMonths(currentMonth, 2));
  const threeMonthsAgo = startOfMonth(subMonths(currentMonth, 3));

  // Filter out internal transfers
  const filteredTransactions = transactions.filter((t) => !isInternalTransfer(t));

  return filteredTransactions.reduce(
    (result, t) => {
      const transactionDate = new UTCDate(t.fspDate);
      // Convert amount to negative if it's not a credit transaction
      // Important: we need to use baseCurrencyAmount in order for the reporting amounts to change when the base conversion is changed
      // TODO: migrate to getNormalizedAmount
      const amount = t.isCredit ? new Big(t.baseCurrencyAmount) : new Big(t.baseCurrencyAmount).times(-1);

      const currentMonthAmount = isSameMonth(transactionDate, currentMonth)
        ? amount.plus(result.currentMonthAmount)
        : new Big(result.currentMonthAmount);
      const previousMonthAmount = isSameMonth(transactionDate, previousMonth)
        ? amount.plus(result.previousMonthAmount)
        : new Big(result.previousMonthAmount);
      const twoMonthsAgoAmount = isSameMonth(transactionDate, twoMonthsAgo)
        ? amount.plus(result.twoMonthsAgoAmount)
        : new Big(result.twoMonthsAgoAmount);
      const threeMonthsAgoAmount = isSameMonth(transactionDate, threeMonthsAgo)
        ? amount.plus(result.threeMonthsAgoAmount)
        : new Big(result.threeMonthsAgoAmount);

      // We only show total for the last 4 months displayed in the UI
      const totalAmount = currentMonthAmount
        .plus(previousMonthAmount)
        .plus(twoMonthsAgoAmount)
        .plus(threeMonthsAgoAmount);

      const isCurrentMonth = isSameMonth(referenceDate, new UTCDate());
      const monthOverMonthIncrease = isCurrentMonth
        ? previousMonthAmount.minus(twoMonthsAgoAmount)
        : currentMonthAmount.minus(previousMonthAmount);

      // We convert the Big numbers to numbers to avoid having to use the type Big anywhere else in the app
      return {
        ...result,
        totalAmount: Number(totalAmount),
        currentMonthAmount: Number(currentMonthAmount),
        previousMonthAmount: Number(previousMonthAmount),
        twoMonthsAgoAmount: Number(twoMonthsAgoAmount),
        threeMonthsAgoAmount: Number(threeMonthsAgoAmount),
        monthOverMonthIncrease: Number(monthOverMonthIncrease),
      };
    },
    {
      currency: baseCurrency,
      totalAmount: 0,
      currentMonthAmount: 0,
      previousMonthAmount: 0,
      twoMonthsAgoAmount: 0,
      threeMonthsAgoAmount: 0,
      monthOverMonthIncrease: 0,
    },
  );
};
